/**
 * Single source of truth for changelog entries and "latest" version.
 * Latest is a numeric timestamp (ms); when we add entries, newest entry has the highest timestamp.
 */

export interface ChangelogEntry {
  date: string;
  timestamp: number;
  version?: string;
  type: 'feature' | 'improvement' | 'fix' | 'update';
  title: string;
  description?: string;
}

export const CHANGELOG_STORAGE_KEY = 'changelogLastSeenVersion';
export const CHANGELOG_LOCAL_STORAGE_KEY = 'changelog_last_seen';

export const changelogEntries: ChangelogEntry[] = [
  {
    date: '2026-06-05',
    timestamp: 1780660800000,
    type: 'feature',
    title: 'Homepage highlights',
    description:
      'You can now hand-pick what shows up first on your help center homepage. Pin a category or article from the structure view, arrange them how you like, and visitors will see them up front in a Highlights section.',
  },
  {
    date: '2026-06-05',
    timestamp: 1780657200000,
    type: 'feature',
    title: 'Table of contents in the sidebar',
    description:
      'Prefer your page outline next to the navigation? You can show the table of contents in the left sidebar so readers can jump between sections without looking to the right. Turn it on in Help Center settings.',
  },
  {
    date: '2026-04-07',
    timestamp: 1744041600000,
    type: 'feature',
    title: 'License Manager localized in 5 languages',
    description: 'The License Manager is now available in English, German, French, Spanish, and Japanese. Use the language switcher in the header to change the interface language.',
  },
  {
    date: '2026-02-05',
    version: '1.0.4',
    timestamp: 1738749600000,
    type: 'feature',
    title: 'Custom domains for Help Centers',
    description: 'Use your own domain for your help center (e.g. help.yourcompany.com). Available on paid plans: go to Help Center settings → Domain, add your domain, then add the CNAME or A record at your DNS provider. Need help? Contact us at hello@folge.me.',
  },
  {
    date: '2026-02-04',
    timestamp: 1738663200000,
    type: 'feature',
    title: 'Custom robots.txt & SEO settings',
    description: 'Control how search engines crawl your help center. In Help Center settings → SEO you can turn search indexing on or off and, on paid plans, define a custom robots.txt (e.g. to block specific paths). Crawlers see your robots.txt at your help center URL + /robots.txt.',
  },
  {
    date: '2026-01-22',
    version: '1.0.3',
    timestamp: 1737547200000,
    type: 'feature',
    title: 'Help Center Search',
    description: 'Full-text search for Help Centers. Press ⌘K (Mac) or Ctrl+K (Windows) to instantly search across articles, guides, and categories. Results are ranked by relevance with highlighted matches.',
  },
  {
    date: '2026-01-22',
    timestamp: 1737543600000,
    type: 'improvement',
    title: 'Faster Page Loads',
    description: 'Help Center pages now load faster with optimized API calls.',
  },
  {
    date: '2026-01-16',
    version: '1.0.2',
    timestamp: 1737043200000,
    type: 'feature',
    title: 'Image Editor',
    description: 'Edit and transform article images with a built-in editor. Crop, rotate, add shapes, arrows, text annotations, and more—all without leaving the editor. Perfect your visuals before publishing.',
  },
  {
    date: '2026-01-16',
    timestamp: 1737039600000,
    type: 'feature',
    title: 'Document Import',
    description: 'Import documents directly as articles into your editor. Simply upload your Word documents (.docx), PDFs, Markdown files, or plain text files and prefill the article with the content of the document.',
  },
  {
    date: '2026-01-12',
    version: '1.0.0',
    timestamp: 1736611200000,
    type: 'improvement',
    title: 'Help Center Launched',
    description: 'Help Center is now available to all users! Create beautiful, searchable knowledge bases for your team and customers. Share guides, articles, and documentation with ease.',
  },
];

export const LATEST_CHANGELOG_VERSION =
  changelogEntries[0]?.timestamp ?? 0;
